import type { GlanceTier } from "./glance";

export interface AreaSearchCandidate {
  key: string;
  name: string;
  tier: GlanceTier;
  tierLabel: string;
  mapped: boolean;
}

export interface LocalSearchResult extends AreaSearchCandidate {
  score: number;
}

const URGENCY_BONUS: Partial<Record<GlanceTier, number>> = {
  off: 10,
  soon: 6,
  later: 3,
};

/** Lowercase, collapse whitespace, and strip diacritics for tolerant local text matching. */
export function normalizeSearchText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function rankAreaMatches(
  query: string,
  candidates: readonly AreaSearchCandidate[],
  limit = 5,
): LocalSearchResult[] {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.replace(/\s+/g, "").length < 2) {
    return [];
  }

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  const scored: LocalSearchResult[] = [];

  for (const candidate of candidates) {
    const normalizedName = normalizeSearchText(candidate.name);
    const nameTokens = normalizedName.split(/[^a-z0-9]+/).filter(Boolean);

    const tokenStartMatch = nameTokens.some((token) => token.startsWith(normalizedQuery));
    const substringMatch = normalizedName.includes(normalizedQuery);
    const tokenPrefixMatch =
      queryTokens.length > 0 && queryTokens.every((queryToken) => nameTokens.some((nameToken) => nameToken.startsWith(queryToken)));

    if (!tokenStartMatch && !substringMatch && !tokenPrefixMatch) {
      continue;
    }

    let score = 60;
    if (tokenStartMatch) {
      score = 100;
    } else if (substringMatch) {
      score = 80;
    }
    score += URGENCY_BONUS[candidate.tier] ?? 0;

    scored.push({ ...candidate, score });
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return a.name.localeCompare(b.name);
  });

  return scored.slice(0, limit);
}
